/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.com/docs/use-static-query/
 */

import { graphql, useStaticQuery } from "gatsby"
import parse from "html-react-parser"
import * as React from "react"
import { Helmet } from "react-helmet"

function Seo({ pageData, authorName, location }) {
  const { wp } = useStaticQuery(
    graphql`
      query {
        wp {
          wpStylesCSS
        }
      }
    `
  )

  // Prevent errors when generating CMS preview (which doesn't fetch all seo data)
  if (!pageData?.seo) {
    return (
      <Helmet
        htmlAttributes={{
          lang: "en",
        }}
      >
        <style type="text/css">{wp.wpStylesCSS}</style>
      </Helmet>
    )
  }

  const {
    title,
    metaDesc,
    metaRobotsNoindex,
    metaRobotsNofollow,
    opengraphTitle,
    opengraphDescription,
    opengraphPublishedTime,
    opengraphModifiedTime,
    opengraphSiteName,
    opengraphType,
    readingTime,
  } = pageData.seo

  const { richSnippetSchemaJson } = pageData

  // build content for robots tag
  const robotsArray = []
  if (metaRobotsNoindex) robotsArray.push(metaRobotsNoindex)
  if (metaRobotsNofollow) robotsArray.push(metaRobotsNofollow)
  const robotsContent = !!robotsArray.length ? robotsArray.join(", ") : ""

  // build content for reading time
  const readingTimeContent = readingTime + " minutes"

  const baseUrl = "https://www.goodhire.com"
  const currentUrl = baseUrl + location.pathname // Using pathname instead of other properties since it's available at build time

  // build content for og:image tags
  const featuredImage = pageData.featuredImage?.node
  const featuredImageUrl = !!featuredImage
    ? baseUrl + featuredImage.localFile.publicURL
    : null
  const featuredImageWidth = !!featuredImage
    ? featuredImage.mediaDetails.width
    : null
  const featuredImageHeight = !!featuredImage
    ? featuredImage.mediaDetails.height
    : null

  const snippetScript = parse(richSnippetSchemaJson)

  return (
    <>
      <Helmet
        htmlAttributes={{
          lang: "en",
        }}
      >
        <title>{title}</title>
        <meta name="robots" content={robotsContent} />
        <meta name="description" content={metaDesc} />

        <meta property="og:locale" content="en_US" />
        <meta property="og:type" content={opengraphType} />
        <meta property="og:title" content={opengraphTitle} />
        <meta property="og:description" content={opengraphDescription} />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:site_name" content={opengraphSiteName} />

        <meta property="og:image" content={featuredImageUrl} />
        <meta property="og:image:width" content={featuredImageWidth} />
        <meta property="og:image:height" content={featuredImageHeight} />

        <meta
          property="article:modified_time"
          content={opengraphModifiedTime}
        />
        <meta
          property="article:published_time"
          content={opengraphPublishedTime}
        />

        <link rel="canonical" href={currentUrl} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:label1" content="Est. reading time" />
        <meta name="twitter:data1" content={readingTimeContent} />
        <meta name="twitter:label2" content="Written By" />
        <meta name="twitter:data2" content={authorName} />

        <style type="text/css">{wp.wpStylesCSS}</style>

        <link
          rel="preload"
          href="/fonts/gotham/woff2/GothamSSm-Bold_Web-Latin.woff2"
          as="font"
          crossOrigin="anonymous"
          type="font/woff2"
        />
        <link
          rel="preload"
          href="/fonts/gotham/woff2/GothamSSm-Medium_Web-Latin.woff2"
          as="font"
          crossOrigin="anonymous"
          type="font/woff2"
        />
        <link
          rel="preload"
          href="/fonts/gotham/woff2/GothamSSm-Book_Web-Latin.woff2"
          as="font"
          crossOrigin="anonymous"
          type="font/woff2"
        />
        {/* Font face CSS inlined so gatsby won't create new font files, breaking the preloading */}
        <style type="text/css">
          {`@font-face {
              src: url("/fonts/source-sans/SourceSansPro-Light-Latin.woff2") format("woff2"),
                url("/fonts/source-sans/SourceSansPro-Light-Latin.woff") format("woff");
              font-family: Source Sans Pro;
              font-style: normal;
              font-weight: 300;
              font-display: swap;
            }

            @font-face {
              src: url("/fonts/source-sans/SourceSansPro-Regular-Latin.woff2") format("woff2"),
                url("/fonts/source-sans/SourceSansPro-Regular-Latin.woff") format("woff");
              font-family: Source Sans Pro;
              font-style: normal;
              font-weight: 400;
              font-display: swap;
            }

            @font-face {
              src: url("/fonts/source-sans/SourceSansPro-Bold-Latin.woff2") format("woff2"),
                url("/fonts/source-sans/SourceSansPro-Bold-Latin.woff") format("woff");
              font-family: Source Sans Pro;
              font-style: normal;
              font-weight: 700;
              font-display: swap;
            }

            @font-face {
              font-family: HCo Gotham SSm;
              src: url("/fonts/gotham/woff2/GothamSSm-Light_Web-Latin.woff2") format("woff2"),
                url("/fonts/gotham/woff/GothamSSm-Light_Web-Latin.woff") format("woff");
              font-weight: 300;
              font-style: normal;
              font-display: swap;
            }

            @font-face {
              font-family: HCo Gotham SSm;
              src: url("/fonts/gotham/woff2/GothamSSm-Book_Web-Latin.woff2") format("woff2"),
                url("/fonts/gotham/woff/GothamSSm-Book_Web-Latin.woff") format("woff");
              font-weight: 400;
              font-style: normal;
              font-display: swap;
            }

            @font-face {
              font-family: HCo Gotham SSm;
              src: url("/fonts/gotham/woff2/GothamSSm-Medium_Web-Latin.woff2") format("woff2"),
                url("/fonts/gotham/woff/GothamSSm-Medium_Web-Latin.woff") format("woff");
              font-weight: 500;
              font-style: normal;
              font-display: swap;
            }

            @font-face {
              font-family: HCo Gotham SSm;
              src: url("/fonts/gotham/woff2/GothamSSm-Bold_Web-Latin.woff2") format("woff2"),
                url("/fonts/gotham/woff/GothamSSm-Bold_Web-Latin.woff") format("woff");
              font-weight: 700;
              font-style: normal;
              font-display: swap;
            }
            `}
        </style>
        <link rel="preconnect" href="https://dev.visualwebsiteoptimizer.com" />
        <script type="application/javascript" id='vwoCode'>
          {`
          window._vwo_code || (function() {
          var account_id=569065,
          version=2.1,
          settings_tolerance=2000,
          hide_element='body',
          hide_element_style = 'opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important;transition:none !important;',
          /* DO NOT EDIT BELOW THIS LINE */
          f=false,w=window,d=document,v=d.querySelector('#vwoCode'),cK='_vwo_'+account_id+'_settings',cc={};try{var c=JSON.parse(localStorage.getItem('_vwo_'+account_id+'_config'));cc=c&&typeof c==='object'?c:{}}catch(e){}var stT=cc.stT==='session'?w.sessionStorage:w.localStorage;code={nonce:v&&v.nonce,library_tolerance:function(){return typeof library_tolerance!=='undefined'?library_tolerance:undefined},settings_tolerance:function(){return cc.sT||settings_tolerance},hide_element_style:function(){return'{'+(cc.hES||hide_element_style)+'}'},hide_element:function(){if(performance.getEntriesByName('first-contentful-paint')[0]){return''}return typeof cc.hE==='string'?cc.hE:hide_element},getVersion:function(){return version},finish:function(e){if(!f){f=true;var t=d.getElementById('_vis_opt_path_hides');if(t)t.parentNode.removeChild(t);if(e)(new Image).src='https://dev.visualwebsiteoptimizer.com/ee.gif?a='+account_id+e}},finished:function(){return f},addScript:function(e){var t=d.createElement('script');t.type='text/javascript';if(e.src){t.src=e.src}else{t.text=e.text}v&&t.setAttribute('nonce',v.nonce);d.getElementsByTagName('head')[0].appendChild(t)},load:function(e,t){var n=this.getSettings(),i=d.createElement('script'),r=this;t=t||{};if(n){i.textContent=n;d.getElementsByTagName('head')[0].appendChild(i);if(!w.VWO||VWO.caE){stT.removeItem(cK);r.load(e)}}else{var o=new XMLHttpRequest;o.open('GET',e,true);o.withCredentials=!t.dSC;o.responseType=t.responseType||'text';o.onload=function(){if(t.onloadCb){return t.onloadCb(o,e)}if(o.status===200||o.status===304){_vwo_code.addScript({text:o.responseText})}else{_vwo_code.finish('&e=loading_failure:'+e)}};o.onerror=function(){if(t.onerrorCb){return t.onerrorCb(e)}_vwo_code.finish('&e=loading_failure:'+e)};o.send()}},getSettings:function(){try{var e=stT.getItem(cK);if(!e){return}e=JSON.parse(e);if(Date.now()>e.e){stT.removeItem(cK);return}return e.s}catch(e){return}},init:function(){if(d.URL.indexOf('__vwo_disable__')>-1)return;var e=this.settings_tolerance();w._vwo_settings_timer=setTimeout(function(){_vwo_code.finish();stT.removeItem(cK)},e);var t;if(this.hide_element()!=='body'){t=d.createElement('style');var n=this.hide_element(),i=n?n+this.hide_element_style():'',r=d.getElementsByTagName('head')[0];t.setAttribute('id','_vis_opt_path_hides');v&&t.setAttribute('nonce',v.nonce);t.setAttribute('type','text/css');if(t.styleSheet)t.styleSheet.cssText=i;else t.appendChild(d.createTextNode(i));r.appendChild(t)}else{t=d.getElementsByTagName('head')[0];var i=d.createElement('div');i.style.cssText='z-index: 2147483647 !important;position: fixed !important;left: 0 !important;top: 0 !important;width: 100% !important;height: 100% !important;background: white !important;display: block !important;';i.setAttribute('id','_vis_opt_path_hides');i.classList.add('_vis_hide_layer');t.parentNode.insertBefore(i,t.nextSibling)}var o=window._vis_opt_url||d.URL,s='https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(o)+'&vn='+version;if(w.location.search.indexOf('_vwo_xhr')!==-1){this.addScript({src:s})}else{this.load(s+'&x=true')}}};w._vwo_code=code;code.init();})();
        `}
        </script>
      </Helmet>

      {snippetScript}
    </>
  )
}

export default Seo
